import React from 'react';
import { CheckSquare, Square, FileText, Users, Target, Edit3, List, MessageCircle, Eye, Wand2, Download, Clock, Globe, Sparkles, AlertCircle, CheckCircle, Copy, ExternalLink, BarChart3, BookOpen, Lightbulb } from 'lucide-react';

function ChineseEssayWritingToolboxPart2(props) {
  const {
    language,
    setLanguage,
    selectedType,
    setSelectedType,
    selectedAudience,
    setSelectedAudience,
    selectedTone,
    setSelectedTone,
    mainTask,
    setMainTask,
    requiredElements,
    setRequiredElements,
    contentPlanner,
    setContentPlanner,
    supportingDetails,
    setSupportingDetails,
    promptBuilder,
    setPromptBuilder,
    selfCheck,
    setSelfCheck,
    activeTab,
    setActiveTab,
    generationType,
    setGenerationType,
    generatedContent,
    setGeneratedContent,
    isGenerating,
    setIsGenerating,
    apiResponse,
    setApiResponse,
    apiError,
    setApiError,
    isPdfGenerating,
    setIsPdfGenerating,
    generatePDF,
    texts,
    t,
    handleCheckboxChange,
    handleContentPlannerChange,
    parseLength,
    generatePrompt,
    callDeepSeekAPI,
    handleGenerate,
    generateTemplate,
    CheckboxItem,
    tabConfigs
  } = props;

  // Professional response presentation component
  const ApiResponseDisplay = ({ response, error }) => {
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-800">
              {language === 'traditional' ? 'API 錯誤' : 'API 错误'}
            </span>
          </div>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      );
    }

    if (!response) return null;

    return (
      <div className="space-y-4">
        {/* Response Metadata */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">
                {language === 'traditional' ? 'AI 生成結果' : 'AI 生成结果'}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-blue-600">
              <Clock className="w-4 h-4" />
              <span>{new Date(response.metadata.timestamp).toLocaleString()}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700">
                {language === 'traditional' ? '字數：' : '字数：'}
                <span className="font-medium">{response.metadata.wordCount}</span>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700">
                {language === 'traditional' ? '段落：' : '段落：'}
                <span className="font-medium">{response.metadata.paragraphCount}</span>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700">
                {language === 'traditional' ? '閱讀時間：' : '阅读时间：'}
                <span className="font-medium">{response.metadata.estimatedReadingTime}分鐘</span>
              </span>
            </div>
          </div>
        </div>

        {/* Generated Content */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">
              {language === 'traditional' ? '生成的文章內容' : '生成的文章内容'}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigator.clipboard.writeText(response.content)}
                className="flex items-center space-x-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                title={language === 'traditional' ? '複製內容' : '复制内容'}
              >
                <Copy className="w-4 h-4" />
                <span>{language === 'traditional' ? '複製' : '复制'}</span>
              </button>
            </div>
          </div>
          <div className="p-4">
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-gray-800 text-sm leading-relaxed font-sans">
                {response.content}
              </pre>
            </div>
          </div>
        </div>

        {/* Usage Information */}
        {response.metadata.usage && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              {language === 'traditional' ? 'API 使用情況' : 'API 使用情况'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">
                  {language === 'traditional' ? '輸入字數：' : '输入字数：'}
                </span>
                <span className="font-medium">{response.metadata.usage.prompt_tokens}</span>
              </div>
              <div>
                <span className="text-gray-600">
                  {language === 'traditional' ? '輸出字數：' : '输出字数：'}
                </span>
                <span className="font-medium">{response.metadata.usage.completion_tokens}</span>
              </div>
              <div>
                <span className="text-gray-600">
                  {language === 'traditional' ? '總字數：' : '总字数：'}
                </span>
                <span className="font-medium">{response.metadata.usage.total_tokens}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-4 mb-4">
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          
          {/* Language Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setLanguage('traditional')}
              className={`flex items-center space-x-1 px-3 py-1 rounded text-sm font-medium transition-colors ${
                language === 'traditional' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>繁體</span>
            </button>
            <button
              onClick={() => setLanguage('simplified')}
              className={`flex items-center space-x-1 px-3 py-1 rounded text-sm font-medium transition-colors ${
                language === 'simplified' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>简体</span>
            </button>
          </div>
        </div>
        <p className="text-gray-600">{t.subtitle}</p>
      </div>

      {/* Parameter Summary Bar */}
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">
          {language === 'traditional' ? '已選擇的參數：' : '已选择的参数：'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-800">
              {language === 'traditional' ? '類型：' : '类型：'}
            </span> 
            <span className="ml-2 text-blue-700">
              {selectedType || (language === 'traditional' ? '未選擇' : '未选择')}
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800">
              {language === 'traditional' ? '受眾：' : '受众：'}
            </span> 
            <span className="ml-2 text-blue-700">
              {selectedAudience || (language === 'traditional' ? '未選擇' : '未选择')}
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800">
              {language === 'traditional' ? '語調：' : '语调：'}
            </span> 
            <span className="ml-2 text-blue-700">
              {selectedTone || (language === 'traditional' ? '未選擇' : '未选择')}
            </span>
          </div>
        </div>
        {requiredElements.length > 0 && (
          <div className="mt-2">
            <span className="font-medium text-blue-800">
              {language === 'traditional' ? '要求：' : '要求：'}
            </span>
            <span className="ml-2 text-blue-700">{requiredElements.join('、')}</span>
          </div>
        )}
        {promptBuilder.length && (
          <div className="mt-2">
            <span className="font-medium text-blue-800">
              {language === 'traditional' ? '長度：' : '长度：'}
            </span>
            <span className="ml-2 text-blue-700">{promptBuilder.length}（≈{parseLength()}字）</span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b">
        {tabConfigs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white border-b-2 border-blue-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-gray-50 rounded-lg p-6">
        {activeTab === 'type' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '1. 文章類型選擇器' : '1. 文章类型选择器'}
            </h2>
            <p className="text-gray-600 mb-4">
              {language === 'traditional' ? '什麼是格式和目標？' : '什么是格式和目标？'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {t.essayTypes.map(type => (
                <CheckboxItem
                  key={type}
                  checked={selectedType === type}
                  onChange={() => setSelectedType(selectedType === type ? '' : type)}
                >
                  {type}
                </CheckboxItem>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'audience' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '2. 受眾與語調' : '2. 受众与语调'}
            </h2>
            
            <div className="mb-6">
              <h3 className="font-medium mb-3">
                {language === 'traditional' ? '您為誰而寫？' : '您为谁而写？'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {t.audiences.map(audience => (
                  <CheckboxItem
                    key={audience}
                    checked={selectedAudience === audience}
                    onChange={() => setSelectedAudience(selectedAudience === audience ? '' : audience)}
                  >
                    {audience}
                  </CheckboxItem>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-3">
                {language === 'traditional' ? '選擇語調：' : '选择语调：'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {t.tones.map(tone => (
                  <CheckboxItem
                    key={tone}
                    checked={selectedTone === tone}
                    onChange={() => setSelectedTone(selectedTone === tone ? '' : tone)}
                  >
                    {tone}
                  </CheckboxItem>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'prompt' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '3. 提示分析' : '3. 提示分析'}
            </h2>
            
            <div className="mb-6">
              <h3 className="font-medium mb-3">
                {language === 'traditional' ? '主要任務：' : '主要任务：'}
              </h3>
              <p className="text-gray-600 mb-2">
                {language === 'traditional' ? '問題或指示是什麼？' : '问题或指示是什么？'}
              </p>
              <textarea
                value={mainTask}
                onChange={(e) => setMainTask(e.target.value)}
                className="w-full p-3 border rounded-lg resize-none"
                rows="3"
                placeholder={language === 'traditional' ? '在此輸入主要任務或問題...' : '在此输入主要任务或问题...'}
              />
            </div>

            <div>
              <h3 className="font-medium mb-3">
                {language === 'traditional' ? '必需元素：' : '必需元素：'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {t.requiredElements.map(element => (
                  <CheckboxItem
                    key={element}
                    checked={requiredElements.includes(element)}
                    onChange={() => handleCheckboxChange(element, requiredElements, setRequiredElements)}
                  >
                    {element}
                  </CheckboxItem>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '4. 重點內容規劃' : '4. 重点内容规划'}
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-medium mb-3">
                  {language === 'traditional' ? '背景/開頭：' : '背景/开头：'}
                </h3>
                <div className="space-y-1">
                  <CheckboxItem
                    checked={contentPlanner.background}
                    onChange={() => handleContentPlannerChange('background')}
                  >
                    {language === 'traditional' ? '設定場景/背景' : '设定场景/背景'}
                  </CheckboxItem>
                  <CheckboxItem
                    checked={contentPlanner.introduceTopic}
                    onChange={() => handleContentPlannerChange('introduceTopic')}
                  >
                    {language === 'traditional' ? '簡要介紹主題' : '简要介绍主题'}
                  </CheckboxItem>
                  <CheckboxItem
                    checked={contentPlanner.stateMainIdea}
                    onChange={() => handleContentPlannerChange('stateMainIdea')}
                  >
                    {language === 'traditional' ? '陳述主要觀點或目的' : '陈述主要观点或目的'}
                  </CheckboxItem>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-3">
                  {language === 'traditional' ? '正文段落：' : '正文段落：'}
                </h3>
                <div className="space-y-1">
                  <CheckboxItem
                    checked={contentPlanner.focusedParagraphs}
                    onChange={() => handleContentPlannerChange('focusedParagraphs')}
                  >
                    {language === 'traditional' ? '每個段落專注於一個主要觀點' : '每个段落专注于一个主要观点'}
                  </CheckboxItem>
                  <div className="ml-6 space-y-1">
                    <CheckboxItem
                      checked={contentPlanner.explanation}
                      onChange={() => handleContentPlannerChange('explanation')}
                    >
                      {language === 'traditional' ? '解釋' : '解释'}
                    </CheckboxItem>
                    <CheckboxItem
                      checked={contentPlanner.example}
                      onChange={() => handleContentPlannerChange('example')}
                    >
                      {language === 'traditional' ? '例子或軼事' : '例子或轶事'}
                    </CheckboxItem>
                    <CheckboxItem
                      checked={contentPlanner.evidence}
                      onChange={() => handleContentPlannerChange('evidence')}
                    >
                      {language === 'traditional' ? '證據或細節' : '证据或细节'}
                    </CheckboxItem>
                    <CheckboxItem
                      checked={contentPlanner.quote}
                      onChange={() => handleContentPlannerChange('quote')}
                    >
                      {language === 'traditional' ? '引文或專家意見（如需要）' : '引文或专家意见（如需要）'}
                    </CheckboxItem>
                  </div>
                  <CheckboxItem
                    checked={contentPlanner.counterarguments}
                    onChange={() => handleContentPlannerChange('counterarguments')}
                  >
                    {language === 'traditional' ? '處理反駁論點或限制（如相關）' : '处理反驳论点或限制（如相关）'}
                  </CheckboxItem>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-3">
                  {language === 'traditional' ? '結論：' : '结论：'}
                </h3>
                <div className="space-y-1">
                  <CheckboxItem
                    checked={contentPlanner.summarize}
                    onChange={() => handleContentPlannerChange('summarize')}
                  >
                    {language === 'traditional' ? '總結主要觀點' : '总结主要观点'}
                  </CheckboxItem>
                  <CheckboxItem
                    checked={contentPlanner.restateOpinion}
                    onChange={() => handleContentPlannerChange('restateOpinion')}
                  >
                    {language === 'traditional' ? '重申意見或建議' : '重申意见或建议'}
                  </CheckboxItem>
                  <CheckboxItem
                    checked={contentPlanner.forwardLooking}
                    onChange={() => handleContentPlannerChange('forwardLooking')}
                  >
                    {language === 'traditional' ? '以前瞻性或反思性陳述結束' : '以前瞻性或反思性陈述结束'}
                  </CheckboxItem>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '5. 支撐細節檢查表' : '5. 支撑细节检查表'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {t.supportingDetails.map(detail => (
                <CheckboxItem
                  key={detail}
                  checked={supportingDetails.includes(detail)}
                  onChange={() => handleCheckboxChange(detail, supportingDetails, setSupportingDetails)}
                >
                  {detail}
                </CheckboxItem>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'language' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '6. 語言工具' : '6. 语言工具'}
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-medium mb-3">
                  {language === 'traditional' ? '實用短語：' : '实用短语：'}
                </h3>
                <div className="bg-white p-4 rounded border space-y-2">
                  <div><strong>{language === 'traditional' ? '引入：' : '引入：'}</strong>「最近…」、「根據…」、「普遍認為…」</div>
                  <div><strong>{language === 'traditional' ? '給出理由：' : '给出理由：'}</strong>「一個原因是…」、「此外…」、「例如…」</div>
                  <div><strong>{language === 'traditional' ? '對比：' : '对比：'}</strong>「然而…」、「另一方面…」</div>
                  <div><strong>{language === 'traditional' ? '結論：' : '结论：'}</strong>「總之…」、「總結…」、「最終…」</div>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-3">
                  {language === 'traditional' ? '連接詞：' : '连接词：'}
                </h3>
                <div className="bg-white p-4 rounded border">
                  <div className="flex flex-wrap gap-2">
                    {(language === 'traditional' 
                      ? ['首先', '其次', '此外', '而且', '另外', '然而', '因此', '結果', '例如', '總結'] 
                      : ['首先', '其次', '此外', '而且', '另外', '然而', '因此', '结果', '例如', '总结']
                    ).map(word => (
                      <span key={word} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'builder' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '7. AI驅動生成器' : '7. AI驱动生成器'}
            </h2>
            <p className="text-gray-600 mb-6">
              {language === 'traditional' ? '根據您選擇的參數自動生成內容，嚴格符合長度要求' : '根据您选择的参数自动生成内容，严格符合长度要求'}
            </p>
            
            {/* Generation Type Selection */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">
                {language === 'traditional' ? '您想生成什麼？' : '您想生成什么？'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex flex-col items-center space-y-2 p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="radio"
                    value="prompt"
                    checked={generationType === 'prompt'}
                    onChange={(e) => setGenerationType(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="text-center">
                    <div className="font-medium">
                      {language === 'traditional' ? '文章提示' : '文章提示'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {language === 'traditional' ? '快速指示' : '快速指示'}
                    </div>
                  </div>
                </label>
                
                <label className="flex flex-col items-center space-y-2 p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="radio"
                    value="template"
                    checked={generationType === 'template'}
                    onChange={(e) => setGenerationType(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Edit3 className="w-8 h-8 text-blue-600" />
                  <div className="text-center">
                    <div className="font-medium">
                      {language === 'traditional' ? '文章模板' : '文章模板'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {language === 'traditional' ? '結構化大綱' : '结构化大纲'}
                    </div>
                  </div>
                </label>
                
                <label className="flex flex-col items-center space-y-2 p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="radio"
                    value="essay"
                    checked={generationType === 'essay'}
                    onChange={(e) => setGenerationType(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Wand2 className="w-8 h-8 text-blue-600" />
                  <div className="text-center">
                    <div className="font-medium">
                      {language === 'traditional' ? '完整文章' : '完整文章'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {language === 'traditional' ? '完整內容' : '完整内容'}
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Auto-filled Parameters Display */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">
                {language === 'traditional' ? '參數（從您的選擇中自動填入）：' : '参数（从您的选择中自动填入）：'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'traditional' ? '類型：' : '类型：'}
                  </label>
                  <input
                    type="text"
                    value={promptBuilder.type}
                    onChange={(e) => setPromptBuilder(prev => ({...prev, type: e.target.value}))}
                    className="w-full p-2 border rounded bg-blue-50"
                    placeholder={language === 'traditional' ? '論文/文章/書信/演講等' : '论文/文章/书信/演讲等'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'traditional' ? '受眾：' : '受众：'}
                  </label>
                  <input
                    type="text"
                    value={promptBuilder.audience}
                    onChange={(e) => setPromptBuilder(prev => ({...prev, audience: e.target.value}))}
                    className="w-full p-2 border rounded bg-blue-50"
                    placeholder={language === 'traditional' ? '您的目標受眾' : '您的目标受众'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'traditional' ? '主題：' : '主题：'}
                  </label>
                  <input
                    type="text"
                    value={promptBuilder.topic}
                    onChange={(e) => setPromptBuilder(prev => ({...prev, topic: e.target.value}))}
                    className="w-full p-2 border rounded"
                    placeholder={language === 'traditional' ? '在此輸入您的主題' : '在此输入您的主题'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'traditional' ? '任務：' : '任务：'}
                  </label>
                  <input
                    type="text"
                    value={promptBuilder.task}
                    onChange={(e) => setPromptBuilder(prev => ({...prev, task: e.target.value}))}
                    className="w-full p-2 border rounded bg-blue-50"
                    placeholder={language === 'traditional' ? '主要任務或指示' : '主要任务或指示'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'traditional' ? '重點：' : '重点：'}
                  </label>
                  <input
                    type="text"
                    value={promptBuilder.keyPoints}
                    onChange={(e) => setPromptBuilder(prev => ({...prev, keyPoints: e.target.value}))}
                    className="w-full p-2 border rounded bg-blue-50"
                    placeholder={language === 'traditional' ? '重點或論點' : '重点或论点'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {language === 'traditional' ? '語調：' : '语调：'}
                  </label>
                  <input
                    type="text"
                    value={promptBuilder.tone}
                    onChange={(e) => setPromptBuilder(prev => ({...prev, tone: e.target.value}))}
                    className="w-full p-2 border rounded bg-blue-50"
                    placeholder={language === 'traditional' ? '正式/友善/說服性等' : '正式/友善/说服性等'}
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">
                  {language === 'traditional' ? '長度要求（嚴格遵循）：' : '长度要求（严格遵循）：'}
                </label>
                <input
                  type="text"
                  value={promptBuilder.length}
                  onChange={(e) => setPromptBuilder(prev => ({...prev, length: e.target.value}))}
                  className="w-full p-2 border rounded border-red-300 bg-red-50"
                  placeholder={language === 'traditional' ? '例如：500字、2頁、300-500字、短段' : '例如：500字、2页、300-500字、短段'}
                />
                <div className="mt-1 text-sm text-red-600">
                  {language === 'traditional' ? '當前目標：' : '当前目标：'}≈{parseLength()}字（解析自：「{promptBuilder.length || (language === 'traditional' ? '未指定' : '未指定')}」）
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div className="mb-6">
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  isGenerating 
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isGenerating ? (
                  <>
                    <Clock className="w-5 h-5 animate-spin" />
                    <span>
                      {language === 'traditional' ? '正在生成' : '正在生成'}
                      {generationType === 'prompt' ? (language === 'traditional' ? '提示' : '提示') : 
                       generationType === 'template' ? (language === 'traditional' ? '模板' : '模板') : 
                       (language === 'traditional' ? '文章' : '文章')}...
                    </span>
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    <span>
                      {language === 'traditional' ? '生成' : '生成'}
                      {generationType === 'prompt' ? (language === 'traditional' ? '提示' : '提示') : 
                       generationType === 'template' ? (language === 'traditional' ? '模板' : '模板') : 
                       (language === 'traditional' ? '完整文章' : '完整文章')}
                      {promptBuilder.length && `（${promptBuilder.length}）`}
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* Generated Content */}
            {generatedContent && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">
                    {language === 'traditional' ? '生成的內容：' : '生成的内容：'}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(generatedContent)}
                      className="flex items-center space-x-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>{language === 'traditional' ? '複製' : '复制'}</span>
                    </button>
                    <button
                      onClick={generatePDF}
                      disabled={isPdfGenerating}
                      className={`flex items-center space-x-1 px-3 py-1 rounded text-sm transition-colors ${
                        isPdfGenerating 
                          ? 'bg-gray-400 text-white cursor-not-allowed' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      title={language === 'traditional' ? '下載PDF文件' : '下载PDF文件'}
                    >
                      {isPdfGenerating ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>{language === 'traditional' ? '生成中...' : '生成中...'}</span>
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4" />
                          <span>PDF</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-white p-4 border rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-gray-800 text-sm leading-relaxed">
                    {generatedContent}
                  </pre>
                </div>
                
                {generationType === 'essay' && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                    <p className="text-green-800 text-sm">
                      <strong>
                        {language === 'traditional' ? '長度合規性：' : '长度合规性：'}
                      </strong>
                      {language === 'traditional' ? 
                        '此文章已生成以嚴格滿足您指定的長度要求' : 
                        '此文章已生成以严格满足您指定的长度要求'
                      }「{promptBuilder.length || (language === 'traditional' ? '標準長度' : '标准长度')}」。
                      {language === 'traditional' ? '實際字數顯示在文章末尾。' : '实际字数显示在文章末尾。'}
                    </p>
                  </div>
                )}
                
                {generationType !== 'prompt' && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-yellow-800 text-sm">
                      <strong>{language === 'traditional' ? '重要：' : '重要：'}</strong>
                      {language === 'traditional' ? 
                        '請在提交前檢查、核實並自定義此生成內容，加入您的具體知識、例子和引用。' : 
                        '请在提交前检查、核实并自定义此生成内容，加入您的具体知识、例子和引用。'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* API Response Display */}
            {apiResponse && (
              <div className="mt-6">
                <ApiResponseDisplay response={apiResponse} error={apiError} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'check' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {language === 'traditional' ? '8. 提交前自我檢查' : '8. 提交前自我检查'}
            </h2>
            <div className="space-y-2">
              {t.selfCheck.map(item => (
                <CheckboxItem
                  key={item}
                  checked={selfCheck.includes(item)}
                  onChange={() => handleCheckboxChange(item, selfCheck, setSelfCheck)}
                >
                  {item}
                </CheckboxItem>
              ))}
            </div>
            
            {selfCheck.length === t.selfCheck.length && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">
                    {language === 'traditional' ? '所有檢查已完成！' : '所有检查已完成！'}
                  </span>
                </div>
                <p className="text-green-700 mt-1">
                  {language === 'traditional' ? '您的文章已準備好提交。' : '您的文章已准备好提交。'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usage Instructions */}
      <div className="mt-8 bg-blue-50 p-6 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-3">
          {language === 'traditional' ? '如何使用此增強的AI工具箱' : '如何使用此增强的AI工具箱'}
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li>{language === 'traditional' ? '在前兩個標籤中選擇您的文章類型、受眾和語調。' : '在前两个标签中选择您的文章类型、受众和语调。'}</li>
          <li>{language === 'traditional' ? '分析您的提示並選擇必需元素。' : '分析您的提示并选择必需元素。'}</li>
          <li>{language === 'traditional' ? '規劃您的內容結構和支撐細節。' : '规划您的内容结构和支撑细节。'}</li>
          <li><strong>{language === 'traditional' ? '指定您的確切長度要求（例如，「500字」、「2頁」、「300-500字」）。' : '指定您的确切长度要求（例如，「500字」、「2页」、「300-500字」）。'}</strong></li>
          <li>{language === 'traditional' ? '選擇要生成的內容：提示、模板（帶字數）或完整文章（確切長度）。' : '选择要生成的内容：提示、模板（带字数）或完整文章（确切长度）。'}</li>
          <li>{language === 'traditional' ? '根據需要檢查和自定義生成的內容。' : '根据需要检查和自定义生成的内容。'}</li>
          <li>{language === 'traditional' ? '在最終提交前完成自我檢查。' : '在最终提交前完成自我检查。'}</li>
        </ol>
        <p className="mt-4 text-blue-700">
          {language === 'traditional' ? 
            'AI生成器現在嚴格遵循您的長度要求並提供確切的字數合規性！' : 
            'AI生成器现在严格遵循您的长度要求并提供确切的字数合规性！'}
        </p>
      </div>
    </div>
  );
}

export default ChineseEssayWritingToolboxPart2;
